import React, { useEffect, useRef, useState } from "react";
import EmojiPicker from "emoji-picker-react";
import GiphySearchBox from "react-giphy-searchbox";
import "./ReplyDialog.css";

const GIPHY_API_KEY = "4OQsubW6vV4uAMHoGaiK8VRRavmxEvYW";

const ReplyDialog = ({
  isOpen,
  onClose,
  post,
  replyTo = null,
  onSubmit,
  mode = "reply",
}) => {
  const isReport = mode === "report";
  const [replyContent, setReplyContent] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedGif, setSelectedGif] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setReplyContent("");
      setSelectedFile(null);
      setSelectedGif(null);
      setShowEmojiPicker(false);
      setShowGifPicker(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [isOpen]);

  if (!isOpen || !post) return null;

  const resetAndClose = () => {
    setReplyContent("");
    setSelectedFile(null);
    setSelectedGif(null);
    setShowEmojiPicker(false);
    setShowGifPicker(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains("reply-dialog-overlay") && !submitting) {
      resetAndClose();
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;
    setSelectedFile(file);
    setSelectedGif(null);
  };

  const handleGifSelect = (gif) => {
    const url = gif?.images?.original?.url || null;
    if (!url) return;
    setSelectedGif(url);
    setSelectedFile(null);
    setShowGifPicker(false);
  };

  const handleEmojiClick = (emojiData) => {
    setReplyContent((prev) => prev + (emojiData?.emoji || ""));
    setShowEmojiPicker(false);
  };

  const handleReplySubmit = async () => {
    if (isReport) {
      if (!replyContent.trim()) {
        alert("Please explain why you are reporting this post.");
        return;
      }
    } else if (!replyContent.trim() && !selectedFile && !selectedGif) {
      alert("Reply text, an image, or a GIF is required.");
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(post.id, {
        content: replyContent,
        file: selectedFile,
        gif: selectedGif,
        parent_reply_id: replyTo?.id || null,
      });
      resetAndClose();
    } catch (err) {
      console.error(isReport ? "Report submit failed:" : "Reply submit failed:", err);
      alert(isReport ? "Failed to submit report." : "Failed to post reply.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="reply-dialog-overlay" onClick={handleOverlayClick}>
      <div className="reply-dialog">
        <button
          className="reply-dialog-close"
          onClick={resetAndClose}
          disabled={submitting}
        >
          ✖
        </button>

        <div className="original-post">
          <strong>
            {isReport
              ? `Reporting @${post.username}`
              : replyTo
              ? `Replying to @${replyTo.username}`
              : `Replying to @${post.username}`}
          </strong>
          <p>
            {(replyTo?.content || post.content || "").length > 200
              ? (replyTo?.content || post.content || "").slice(0, 200) + "..."
              : (replyTo?.content || post.content || "")}
          </p>
        </div>

        <textarea
          className="reply-textarea"
          value={replyContent}
          onChange={(e) => setReplyContent(e.target.value)}
          placeholder={
            isReport
              ? "Explain why you are reporting this post..."
              : "Write your reply..."
          }
        />

        {!isReport && selectedFile && (
          <div style={{ marginTop: "8px", fontSize: "0.9rem" }}>
            📷 {selectedFile.name}
            <button
              type="button"
              onClick={() => {
                setSelectedFile(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
              }}
              style={{ marginLeft: "8px" }}
            >
              Remove
            </button>
          </div>
        )}

        {!isReport && selectedGif && (
          <div style={{ marginTop: "8px", textAlign: "center" }}>
            <img
              src={selectedGif}
              alt="Selected GIF"
              style={{ maxWidth: "100%", maxHeight: "180px", borderRadius: "8px" }}
            />
            <button
              type="button"
              onClick={() => setSelectedGif(null)}
              style={{ display: "block", margin: "6px auto 0" }}
            >
              Remove GIF
            </button>
          </div>
        )}

        <div
          className="reply-action-buttons"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
            marginTop: "10px",
          }}
        >
          {!isReport && (
            <>
              <button
                type="button"
                title="Upload Image"
                onClick={() => fileInputRef.current?.click()}
                disabled={submitting}
              >
                📷
              </button>

              <button
                type="button"
                title="Add GIF"
                onClick={() => {
                  setShowGifPicker((prev) => !prev);
                  setShowEmojiPicker(false);
                }}
                disabled={submitting}
              >
                🎞
              </button>
            </>
          )}

          <button
            type="button"
            title="Insert Emoji"
            onClick={() => {
              setShowEmojiPicker((prev) => !prev);
              setShowGifPicker(false);
            }}
            disabled={submitting}
          >
            😊
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />

          <div style={{ flex: "1 1 auto" }} />

          <button
            className="reply-submit-btn"
            onClick={handleReplySubmit}
            disabled={submitting}
          >
            {submitting
              ? isReport
                ? "Submitting..."
                : "Replying..."
              : isReport
              ? "Submit Report"
              : "Reply"}
          </button>
        </div>

        {!isReport && showGifPicker && (
          <div
            style={{
              marginTop: "10px",
              padding: "10px",
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: "8px",
            }}
          >
            <GiphySearchBox
              apiKey={GIPHY_API_KEY}
              onSelect={handleGifSelect}
            />
          </div>
        )}

        {showEmojiPicker && (
          <div style={{ marginTop: "10px" }}>
            <EmojiPicker onEmojiClick={handleEmojiClick} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ReplyDialog;
